import { Col, Form, Row } from 'antd';
import { Store } from 'antd/lib/form/interface';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import checkLeiApi from 'services/akcionar/emise/checkLei';
import identifikaceEmitenta, { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import isinRozcestnikApi, { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import { isinZmenaPost } from 'services/akcionar/emise/zmenaISIN';
import { IcisStaty } from 'services/cisStaty';

import leiValidation from 'utils/validations/leiValidation';

import { IRozcestnikInput } from '../novyISIN/IdentifikaceEmitentaNovy';

interface IRozcestnik {
    cisStaty: IcisStaty[];
    nextStep: () => void;
    rozcestnikData: IRozcestnikData['spolecnost'] | undefined;
    setRozcestnikData: React.Dispatch<React.SetStateAction<IRozcestnikData['spolecnost'] | undefined>>;
    setRozcestnikInput: React.Dispatch<React.SetStateAction<IRozcestnikInput | undefined>>;
    setZadostID: (value: number) => void;
    identifikacePutData: IIdentifikaceEmitenta | undefined;
    typZruseni: boolean;
}

const RozcestnikZmena: React.FC<IRozcestnik> = ({
    cisStaty,
    nextStep,
    rozcestnikData,
    setRozcestnikData,
    setRozcestnikInput,
    setZadostID,
    typZruseni
}) => {
    const [form] = Form.useForm();
    const { aktivniFirma, profily } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [leiCheck, setLeiCheck] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>('');

    useEffect(() => {
        form.setFieldsValue({
            stat: aktivniFirma?.stat?.toUpperCase()
        });
    }, [aktivniFirma?.stat, form]);

    const onFinish = (value: Store) => {
        setRozcestnikInput({
            emitentje: value.emitentje,
            evidencecp: value.evidencecp,
            podobacp: value.podobacp
        });
        setLoading(true);
        if (!leiCheck) {
            isinRozcestnikApi({
                // sluzba rozhodne ci user prejde na dalsiu stranku alebo LEIcheck
                zadao: typZruseni ? 'ZRUS' : 'ZMEN',
                idmb: value.idmb,
                stat: value.stat
            })
                .then(resp => {
                    setRozcestnikData(resp.spolecnost);
                    if (resp.nasledujeweb === 'ISINLEI') {
                        setLeiCheck(true);
                    } else if (resp.nasledujeweb === 'CEKA' || resp.nasledujeweb === 'CEKALEI') {
                        setOpenModal(true);
                        setModalMessage('FORMULARE_CEKANI_NA_ZPRACOVANI_AMLZ');
                    } else nextStep();
                    isinZmenaPost(value.idmb, value.stat, typZruseni ? 'Zruseni' : 'Zmena').then(data => {
                        identifikaceEmitenta(
                            'PUT',
                            data.idzadosti,
                            {
                                ...resp.spolecnost,
                                pscm: String(resp.spolecnost.pscm) || '',
                                idmb: value.idmb,
                                stat: value.stat,
                                emailkont: profily!.email,
                                telefon: profily!.telefon1,
                                jmeno: profily!.jmeno,
                                prijmeni: profily!.prijmeni
                            },
                            typZruseni ? 'Zruseni' : 'Zmena'
                        );
                        setZadostID(data.idzadosti);
                        nextStep();
                    });
                })
                .finally(() => setLoading(false));
        } else {
            checkLeiApi(value.lei)
                .then(data => {
                    if (
                        (value.stat === 'CZ' && data.ic !== value.idmb) ||
                        (value.stat !== 'CZ' && value.stat !== data.stat)
                    ) {
                        setOpenModal(true);
                        setModalMessage('FORMULARE_LEI_VEDENO_PRO_JINOU_SPOL');
                    } else if (data?.stav !== 'ISSUED' && data?.stav !== 'PENDING_TRANSFER') {
                        setOpenModal(true);
                        setModalMessage(data.stav === 'LAPSED' ? 'FORMULARE_LEI_LAPSED_FO' : 'FORMULARE_LEI_ZRUSENE');
                    } else {
                        setRozcestnikData({
                            idmb: data.ic,
                            stat: data.stat,
                            lei: data.lei,
                            nazev: data.spolecnost,
                            ...rozcestnikData
                        });
                        nextStep();
                    }
                })
                .catch(() => {
                    setOpenModal(true);
                    setModalMessage('FORMULARE_AMLZ_ROZCESTNIK_INVALID_LEI');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish}>
                {!typZruseni && (
                    // eslint-disable-next-line max-len
                    <Paragraph text="ISIN_ZMENA_NADPIS_DOPLNENI" />
                )}
                <Title text="ZAKLADNI_UDAJE_O_EMITENTOVI" level={4} />
                <InputComponent
                    disabled={true}
                    initialValue={aktivniFirma?.ic}
                    label="IDENTIFIKACNI_CISLO"
                    name="idmb"
                    notRequired={true}
                    type="text"
                />
                <SelectComponent
                    disabled={true}
                    placeholder={aktivniFirma?.stat?.toUpperCase()}
                    defaultValue={aktivniFirma?.stat?.toUpperCase()}
                    isFormItem={true}
                    notRequired={true}
                    data={cisStaty}
                    name="stat"
                    labelParagraph={'STAT_SIDLA'}
                />
                {leiCheck && (
                    <InputComponent
                        label="LEI_CELE"
                        name="lei"
                        type="text"
                        rules={leiValidation()}
                        initialValue={rozcestnikData?.lei}
                    />
                )}
                <Modal
                    title={<TranslateText>NEVALIDNI_LEI</TranslateText>}
                    open={openModal}
                    onCancel={() => setOpenModal(false)}
                    afterClose={() => {
                        setOpenModal(false);
                        setLoading(false);
                    }}
                    footer={
                        <Row>
                            <ButtonComponent
                                buttontext="ZAVRIT"
                                type="default"
                                onClick={() => {
                                    setOpenModal(false);
                                    setLoading(false);
                                }}
                                id="modalZpetBtn"
                            />
                        </Row>
                    }
                >
                    <Row>
                        <Paragraph text={modalMessage} />
                    </Row>
                </Modal>

                <Row className="button-row">
                    {leiCheck && (
                        <Col>
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => setLeiCheck(false)}
                                type="default"
                                id="zpetBtn"
                            />
                        </Col>
                    )}
                    <Col>
                        <ButtonComponent buttontext="POKRACOVAT" onClick={() => form.submit()} id="pokracovatBtn" />
                    </Col>
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default RozcestnikZmena;
